import { SubsessionStatus, SubsessionUserStatus } from 'zoom/subsessionSdkEnums'
import { MessageWrapper, StatusPanel } from '.'
import { BreakoutRoomsState } from 'pages/useBreakouts'
import { BookingContext } from 'contexts/booking'
import { useContext } from 'react'
import { useFeature } from '@growthbook/growthbook-react'
import { ConferenceContext } from 'contexts/conference'
import { isGocAssistant } from 'utils/is-goc-assistant'

type BreakoutStatusProps = {
    breakouts?: BreakoutRoomsState
    isSubsessionsEnabled: boolean
    isStudent: boolean
}

const isStudentJoiningBreakout = ({ breakouts, isSubsessionsEnabled, isStudent }: BreakoutStatusProps): boolean =>
    isSubsessionsEnabled &&
    isStudent &&
    breakouts?.subsessionStatus === SubsessionStatus.InProgress &&
    breakouts?.userStatus !== SubsessionUserStatus.InSubsession

const WaitingForOthers: React.FC<{ breakouts?: BreakoutRoomsState }> = ({ breakouts }) => {
    const conference = useContext(ConferenceContext)
    const booking = useContext(BookingContext)
    const isSubsessionsEnabled = useFeature('goc-sub-session').value as boolean
    const sessionJoined = conference.status === 'session-joined'
    const isStudent = booking.status === 'validated' && booking.user.role === 'student'

    // Show "Leaving" message during subsession closure transition
    if (isSubsessionsEnabled && breakouts?.subsessionStatus === SubsessionStatus.Closing) {
        return (
            <StatusPanel id='leaving-breakout-room-panel'>
                <MessageWrapper>Leaving breakout room...</MessageWrapper>
            </StatusPanel>
        )
    }

    // Show "Joining" message for students while they are physically between rooms
    if (isStudentJoiningBreakout({ breakouts, isSubsessionsEnabled, isStudent })) {
        return (
            <StatusPanel id='joining-breakout-room-panel'>
                <MessageWrapper>Joining breakout room...</MessageWrapper>
            </StatusPanel>
        )
    }

    const currentRoom = sessionJoined ? breakouts?.getCurrentRoom() : null
    const roomUsersIds = sessionJoined && (currentRoom || currentRoom === 0) ? breakouts?.rooms[currentRoom].users : null
    const roomUsers = roomUsersIds
        ? conference.participants.filter(p =>
            // render teacher if in the room
            (currentRoom === breakouts?.teacherRoom && p.role === 'teacher') ||
            // render students in the room
            (roomUsersIds.includes(p.wseUserId)))
        : null
    const participants = (roomUsers || conference.participants).filter(p => !isGocAssistant(p))

    // If subsessions are enabled, the teacher is in oversee mode (currentRoom === null),
    // and there are users assigned to breakouts, we shouldn't show "Waiting for others"
    const hasUsersInBreakouts = isSubsessionsEnabled && breakouts?.isActive && currentRoom === null &&
        breakouts.rooms.some(r => r.users.length > 0)

    if (participants.length <= 1 && !hasUsersInBreakouts) {
        return (
            <StatusPanel>
                <MessageWrapper>Waiting for others to join</MessageWrapper>
            </StatusPanel>
        )
    } else return null
}

export { WaitingForOthers }
